import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../service/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Add auth state listener
    const handleAuthStateChange = (userData) => {
      try {
        console.log('AuthContext: Auth state changed, userData:', userData);
        setUser(userData);
        setError(null);
        setLoading(false);
      } catch (error) {
        console.error('Error in auth state change handler:', error);
        setError(error.message || 'Authentication state error');
        setLoading(false);
      }
    };

    // Initialize auth service and wait for Firebase Auth to be ready
    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Initializing auth service...');
        await authService.initialize();
        
        // Check if there's a stored session
        if (authService.restoreSession()) {
          const user = authService.getCurrentUser();
          console.log('AuthContext: Restored session, user:', user);
          setUser(user);
        }
        
        console.log('AuthContext: Auth initialization complete, setting loading to false');
        setLoading(false);
      } catch (error) {
        console.error('Auth initialization error:', error);
        setError(error.message || 'Authentication initialization failed');
        setLoading(false);
      }
    };

    authService.addAuthStateListener(handleAuthStateChange);
    initializeAuth();

    // Cleanup listener on unmount
    return () => {
      authService.removeAuthStateListener(handleAuthStateChange);
    };
  }, []);

  const signIn = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call Firebase sign-in directly
      await authService.signIn();
    } catch (error) {
      setError(error.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const signOut = () => {
    authService.signOut();
    setUser(null);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    clearError,
    isAuthenticated: !!user,
    isSignedIn: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
